import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './ImagePreviewsGrid.css';

/**
 * ImagePreviewsGrid - Shared component for displaying image previews with remove buttons
 * Used by CreatePostPage and CreateMarketplaceObjectPage
 *
 * @param {Array} previews - Array of preview objects with {id, src} or string URLs
 * @param {function} onRemove - Callback when remove button is clicked (receives index or id)
 * @param {string} label - Optional label for the section (set to empty string to hide)
 * @param {boolean} showLabel - Whether to show the label (default: false)
 * @param {boolean} showPreviewBadge - Whether to show "Preview" badge on first image (default: false)
 */
const ImagePreviewsGrid = ({ previews = [], onRemove, label, showLabel = false, showPreviewBadge = false }) => {
  const { t } = useLanguage();

  if (previews.length === 0) return null;

  const defaultLabel = t('imagePreviewsGrid.selectedImages') || 'Ausgewählte Bilder';
  const displayLabel = label || defaultLabel;

  return (
    <div className="image-previews-grid-section">
      {showLabel && displayLabel && (
        <span className="image-previews-grid-label">
          {displayLabel} ({previews.length})
        </span>
      )}
      <div
        className="image-previews-grid-container"
        role="list"
        aria-label={`${t('imagePreviewsGrid.selectedImages') || 'Ausgewählte Bilder'} ${previews.length}`}
      >
        {previews.map((preview, index) => {
          // Support both string URLs and objects with {id, src}
          const src = typeof preview === 'string' ? preview : preview.src;
          const id = typeof preview === 'string' ? index : (preview.id || index);

          return (
            <div key={id} className="image-preview-item" role="listitem">
              <img
                src={src}
                alt={`${t('imagePreviewsGrid.preview') || 'Vorschau'} ${index + 1}`}
                className="image-preview-thumbnail"
              />
              {/* Issue #226: Show "Preview" badge on first image */}
              {showPreviewBadge && index === 0 && (
                <span
                  className="image-preview-badge"
                  aria-label={t('imagePreviewsGrid.previewImageBadge') || 'Dieses Bild wird als Vorschau verwendet'}
                >
                  {t('imagePreviewsGrid.preview') || 'Vorschau'}
                </span>
              )}
              <button
                type="button"
                className="image-preview-remove-btn"
                onClick={(e) => { e.stopPropagation(); onRemove(typeof preview === 'string' ? index : id); }}
                aria-label={`${t('imagePreviewsGrid.removeImage') || 'Bild entfernen'} ${index + 1}`}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImagePreviewsGrid;
